"use client";

import { useSectionViewEvent } from "@/hooks/use-section-view-event";
import { piEvents } from "@/lib/analytics";
import { PI_SECTION } from "@/lib/analytics/schema";
import { faqItems } from "@/lib/faq-data";

/** Stable, readable id for a question — GA reports on it instead of the full text. */
const toFaqId = (question: string, position: number) =>
  question
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40) || `faq_${position}`;

export function FaqSection() {
  useSectionViewEvent({
    sectionId: "faq",
    sectionName: PI_SECTION.FAQ.name,
    sectionPosition: PI_SECTION.FAQ.position,
  });

  return (
    <section id="faq" className="bg-pi-bg">
      <div className="mx-auto max-w-[820px] px-4 py-14 sm:px-6 lg:px-8 lg:py-24">
        <h2 className="m-0 text-[clamp(26px,4vw,34px)] font-semibold leading-tight text-pi-bright">
          Frequently asked questions
        </h2>
        <div className="mt-8 flex flex-col gap-3">
          {faqItems.map((item, index) => (
            <details
              key={item.question}
              onToggle={(event) =>
                piEvents.faqToggled({
                  faqId: toFaqId(item.question, index + 1),
                  faqQuestion: item.question,
                  faqPosition: index + 1,
                  action: event.currentTarget.open ? "expanded" : "collapsed",
                })
              }
              className="group overflow-hidden rounded-[14px] border border-pi-border bg-pi-elevated"
            >
              <summary className="flex cursor-pointer items-center justify-between gap-4 px-[22px] py-5 text-[17px] font-semibold text-pi-bright">
                <span className="min-w-0 break-words">{item.question}</span>
                <span
                  aria-hidden="true"
                  className="shrink-0 text-2xl leading-none text-pi-accent transition-transform group-open:rotate-45"
                >
                  +
                </span>
              </summary>
              <div className="px-[22px] pb-5 text-[15px] leading-relaxed break-words text-pi-text">
                {item.answer}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
